/**
 * Pre-solved topologies — gateway detection + rigid sub-assemblies.
 *
 * Certain local shapes have a known-good layout that emergent forces only
 * approximate: a clean 1-N fanout (hub calling N children) wants its
 * children evenly spaced at ideal edge length, centered on the hub.
 * Instead of hoping springs settle there, we detect the shape, PRE-SOLVE
 * its internal geometry from a template, and hand the whole formation to
 * the global solver as ONE super-node (the assembly's bounding box).
 * Every external force on any member acts on the body; the body moves as
 * one; its pose stays fully dynamic — the solver only lost the freedom to
 * deform the formation's inside, which is the point. Members are expanded
 * back to their slots at the output boundary.
 *
 * A hub is a GATEWAY for its neighbors when it is the only route between
 * them. Each child owns a TERRITORY — the directed closure away from the
 * hub (downstream for out-children, upstream for in-children), which may
 * carry a whole world: chains, trees, even internal cycles. A child is
 * GUILTY when its own territory contains a sibling: its world builds a
 * path around the hub, so it can't share the wing template. Guilt is
 * asymmetric — the contained sibling stays innocent. Refinements:
 *
 * - Direction picks the wing: hub → X puts X on the out-wing (right),
 *   X → hub on the in-wing (left). A two-way connection counts as
 *   incoming.
 * - A guilty child implicating exactly ONE same-side sibling that
 *   implicates nobody else is a tolerated entangled pair (it still
 *   stacks cleanly). Anything wider POISONS that whole side — the hub
 *   is not the sole route between that side's children, so no wing
 *   templates there, innocent members included. A cycle through the hub
 *   (an in-child's upstream world containing an out-sibling) poisons
 *   both sides.
 * - A FORWARD BYPASS — an in-child whose downstream world (away from
 *   the hub) reaches an out-sibling — is a path around the hub parallel
 *   to the in→hub→out flow. Guilt can't see it (in-territories look
 *   upstream, out-territories downstream) and the outside-connection
 *   cap would tolerate it as one external edge on each endpoint. The
 *   violation is confined to the pair, so both endpoints are EXCLUDED
 *   from the wings (their edges stay globally solved) while innocent
 *   siblings keep the formation.
 * - Two subtrees converging on shared infrastructure OUTSIDE the family
 *   don't entangle — the outside-connection cap governs those by
 *   exclusion, without poisoning.
 * - A child with more than one outside connection (callers beyond the
 *   hub for out-children, callees for in-children, its own world
 *   excluded) is shared infrastructure — templating it under one hub
 *   would misplace it; it stays globally solved.
 * - Wings start at ONE child, but a lone full-size child stays organic —
 *   pairs are for satellites (PAIR_LEAF_MAX_AREA_FRAC).
 *
 * Wings claim children greedily, smallest wing first (a dedicated caller
 * is the likelier owner of a shared child than a busy orchestrator);
 * inout gateways break size ties so chain interiors claim both their
 * neighbors. A claimed child that already anchored its own strictly
 * smaller assembly brings it along: buildAssemblies flattens the child
 * assembly into the wing slot (fan-of-fans), so the solver still sees
 * plain flat assemblies.
 */

import { NODE_W, NODE_H, PAIR_LEAF_MAX_AREA_FRAC } from "./layoutParams.js";

export interface Motif {
  readonly anchorId: string;
  /** Claimed children, sorted by id (deterministic). ≥ 1. */
  readonly leafIds: readonly string[];
  /** "out": hub → children (column right of hub); "in": sources → hub. */
  readonly side: "out" | "in";
}

/**
 * A SINK SHELF — the second pre-solved topology. A rigid stack of pure
 * sinks that share callers, with NO hub member: the stack is furniture
 * that belongs to nobody, positioned purely by its callers' springs.
 * Structurally immune to the rump-wing failure mode: members are pure
 * sinks, so ALL their edges become springs on the body — no unmodeled
 * edge can stab into the formation. Coarsening a shelf also cannot
 * distort the solver's global metric (no shortest path routes THROUGH a
 * sink).
 *
 * Eligibility per member: pure sink, 2..SHELF_MAX_CALLERS callers
 * (1-caller sinks are wing/pair material; more is infrastructure),
 * unclaimed by a gateway assembly. Grouping is GREEDY-WITH-REMOVAL by
 * patron — the caller sharing the most eligible remaining members wins
 * them all (per-member argmax degenerates into singletons on real
 * graphs). Shelves need ≥ SHELF_MIN_MEMBERS.
 */
export interface Shelf {
  /** The caller that grouped this shelf (deterministic; NOT an owner). */
  readonly patronId: string;
  /** Members sorted by (second-caller id, id) — clusters same-second-
   *  caller neighbors so their external edges arrive combed. ≥ 2. */
  readonly memberIds: readonly string[];
}

/** Shelf member caller range. ≥4 callers = infrastructure, stays free. */
const SHELF_MAX_CALLERS = 3;
/** Below two members a shelf is not a shelf. Call sites must re-check
 *  after their pin/port filters (a pinned member must not leave a 1-node
 *  rigid box behind). */
export const SHELF_MIN_MEMBERS = 2;

/**
 * Detect sink shelves among nodes not already claimed by gateway
 * assemblies. Deterministic under input permutation.
 *
 * `exactTwins` restricts detection to TRUE TWINS — sinks with byte-equal
 * caller sets. Twins run BEFORE gateway detection: a lone twin can anchor
 * a fan-in of the shared callers (their upstream territories are clean —
 * the entanglement runs through the DOWNSTREAM sibling, invisible to
 * upstream closures), which arbitrarily crowns one twin and strands the
 * others. The symmetric stack is the honest formation, so twins reserve
 * their members first; patron-overlap shelves still run after gateways.
 *
 * Neighborhood mode forms NO overlap shelves — call sites skip this pass
 * and keep only twins. An overlap yoke grids sinks whose remaining
 * callers disagree; a rigid grid satisfies one pull direction, so each
 * member strands ×6-12 from its other caller (measured: SessionCache/
 * EventLake). A yoke whose pulls DO agree is byte-equal caller sets —
 * i.e. a twin class the twin pass already reserved, so "overlap shelves
 * restricted to agreeing members" is exactly the empty set. Contract:
 * proximity (C1) outranks deliberate alignment (C2). The permissive
 * overlap pass dies with the global solve (P3).
 */
export function detectShelves(
  ids: readonly string[],
  edges: readonly { from: string; to: string }[],
  /** Every node already participating in an earlier formation (gateway
   *  anchors and leaves, twin-shelf members). */
  claimed: ReadonlySet<string>,
  opts?: { readonly exactTwins?: boolean },
): Shelf[] {
  const idSet = new Set(ids);
  const dirSeen = new Set<string>();
  const outDeg = new Map<string, number>();
  const callersOf = new Map<string, Set<string>>();
  for (const id of ids) {
    outDeg.set(id, 0);
    callersOf.set(id, new Set());
  }
  for (const e of edges) {
    if (e.from === e.to || !idSet.has(e.from) || !idSet.has(e.to)) continue;
    const key = `${e.from}|${e.to}`;
    if (dirSeen.has(key)) continue;
    dirSeen.add(key);
    outDeg.set(e.from, outDeg.get(e.from)! + 1);
    callersOf.get(e.to)!.add(e.from);
  }

  const eligible = new Set<string>();
  for (const id of ids) {
    if (claimed.has(id)) continue;
    if (outDeg.get(id)! !== 0) continue; // not a pure sink (incl. two-way)
    const n = callersOf.get(id)!.size;
    if (n >= SHELF_MIN_MEMBERS && n <= SHELF_MAX_CALLERS) eligible.add(id);
  }

  const shelves: Shelf[] = [];
  if (opts?.exactTwins) {
    // Group by byte-equal caller set; only groups of ≥ 2 are twins.
    const byKey = new Map<string, string[]>();
    for (const m of [...eligible].sort()) {
      const key = [...callersOf.get(m)!].sort().join("|");
      const arr = byKey.get(key) ?? [];
      arr.push(m);
      byKey.set(key, arr);
    }
    for (const key of [...byKey.keys()].sort()) {
      const members = byKey.get(key)!;
      if (members.length < SHELF_MIN_MEMBERS) continue;
      shelves.push({
        patronId: key.split("|")[0]!,
        memberIds: members, // twins: identical second-callers, id order
      });
    }
    shelves.sort((a, b) =>
      a.memberIds[0]! < b.memberIds[0]! ? -1 : a.memberIds[0]! > b.memberIds[0]! ? 1 : 0,
    );
    return shelves;
  }
  while (eligible.size > 0) {
    // Greedy-with-removal: the caller sharing the most remaining eligible
    // members wins them all. Ties: lexicographic caller id.
    const countOf = new Map<string, number>();
    for (const m of eligible) {
      for (const c of callersOf.get(m)!) {
        countOf.set(c, (countOf.get(c) ?? 0) + 1);
      }
    }
    let patron: string | undefined;
    let best = 0;
    for (const c of [...countOf.keys()].sort()) {
      const n = countOf.get(c)!;
      if (n > best) {
        best = n;
        patron = c;
      }
    }
    if (patron === undefined || best < SHELF_MIN_MEMBERS) break;
    const members = [...eligible].filter((m) => callersOf.get(m)!.has(patron));
    const secondOf = (m: string): string =>
      [...callersOf.get(m)!].filter((c) => c !== patron).sort()[0] ?? "";
    members.sort((a, b) => {
      const sa = secondOf(a);
      const sb = secondOf(b);
      return sa < sb ? -1 : sa > sb ? 1 : a < b ? -1 : a > b ? 1 : 0;
    });
    for (const m of members) eligible.delete(m);
    shelves.push({ patronId: patron, memberIds: members });
  }
  // Stable output order by patron.
  shelves.sort((a, b) =>
    a.patronId < b.patronId ? -1 : a.patronId > b.patronId ? 1 : 0,
  );
  return shelves;
}

/**
 * Reserve NEAR-TWIN sinks so no lone sibling crowns a fan-in over shared
 * callers. Two shelf-eligible pure sinks (2–3 callers) are near-twins when
 * their caller sets share ≥ 2 callers but are not byte-equal (byte-equal =
 * exact twins, reserved by the twin pass). The twin pass only
 * catches identical caller sets; near-twins (o1 {a,c,d}, o2 {a,c,e}) slip
 * through, and gateway detection crowns whichever sorts first as an in-wing
 * hub over the shared callers, stranding the sibling — id-order arbitrary.
 * Both conflicting sinks reserve: neither anchors nor is a leaf, so their
 * shared callers stay free and both siblings place organically, symmetric
 * and id-stable. They form NO shelf — shelving divergent-caller sinks
 * together is the overlap yoke deleted from neighborhood mode (see the
 * detectShelves header). A sink with no near-twin sibling is untouched and
 * still anchors its legitimate fan-in.
 */
export function nearTwinReserved(
  ids: readonly string[],
  edges: readonly { from: string; to: string }[],
  /** Nodes already reserved by the exact-twin pass — folded into the
   *  result so callers can union a single reservation set. */
  twinReserved: ReadonlySet<string>,
): Set<string> {
  const idSet = new Set(ids);
  const dirSeen = new Set<string>();
  const outDeg = new Map<string, number>();
  const callersOf = new Map<string, Set<string>>();
  for (const id of ids) {
    outDeg.set(id, 0);
    callersOf.set(id, new Set());
  }
  for (const e of edges) {
    if (e.from === e.to || !idSet.has(e.from) || !idSet.has(e.to)) continue;
    const key = `${e.from}|${e.to}`;
    if (dirSeen.has(key)) continue;
    dirSeen.add(key);
    outDeg.set(e.from, outDeg.get(e.from)! + 1);
    callersOf.get(e.to)!.add(e.from);
  }

  // Shelf-eligible pure sinks, in id order for determinism.
  const eligible: string[] = [];
  for (const id of [...ids].sort()) {
    if (outDeg.get(id)! !== 0) continue; // not a pure sink (incl. two-way)
    const n = callersOf.get(id)!.size;
    if (n >= SHELF_MIN_MEMBERS && n <= SHELF_MAX_CALLERS) eligible.push(id);
  }

  const reserved = new Set(twinReserved);
  for (let i = 0; i < eligible.length; i++) {
    const ci = callersOf.get(eligible[i]!)!;
    for (let j = i + 1; j < eligible.length; j++) {
      const cj = callersOf.get(eligible[j]!)!;
      let shared = 0;
      for (const c of ci) if (cj.has(c)) shared++;
      // ≥ 2 shared callers, and not byte-equal (exact twins already
      // reserved). One shared caller is a single external edge — tolerated
      // by CHILD_MAX_EXTERNAL, so a lone shared source never triggers.
      // Two-or-more overwhelms the ext cap: crowning one sink strands the
      // sibling by ≥ 2 edges, the id-order asymmetry this reserves against.
      if (shared >= 2 && !(shared === ci.size && shared === cj.size)) {
        reserved.add(eligible[i]!);
        reserved.add(eligible[j]!);
      }
    }
  }
  return reserved;
}

/** Max connections a child may have to nodes beyond the hub and its own
 *  world. More = shared infrastructure (a DB, a logger) — stays free. */
const CHILD_MAX_EXTERNAL = 1;

/** Max nodes a child may drag along (territory beyond itself). A small
 *  dangle (a processor and its vendor, a short chain, a little loop)
 *  rides in the wing; a child anchoring a big world is load-bearing
 *  structure — templating it under one hub would yank its whole
 *  neighborhood, and cycle cores would even launder their callers into
 *  "own world" past the external cap. */
const CHILD_MAX_WORLD = 3;

/**
 * Detect gateway motifs (see the file header for the full rule set).
 */
export function detectMotifs(
  ids: readonly string[],
  edges: readonly { from: string; to: string }[],
  /** Node sizes — used to restrict 1-leaf PAIR wings to satellite-sized
   *  leaves (see PAIR_LEAF_MAX_AREA_FRAC). Omitted (tests, callers
   *  without geometry): every leaf counts as a satellite. */
  sizes?: ReadonlyMap<string, { w: number; h: number }>,
  /** Nodes already claimed by an earlier formation (true-twin shelves):
   *  they can neither anchor a wing nor be claimed as a leaf, but their
   *  edges still shape territories, guilt, and caps — they remain real
   *  topology. */
  reserved?: ReadonlySet<string>,
): Motif[] {
  const idSet = new Set(ids);
  const dirSeen = new Set<string>();
  const unique: { from: string; to: string }[] = [];
  for (const e of edges) {
    if (e.from === e.to || !idSet.has(e.from) || !idSet.has(e.to)) continue;
    const key = `${e.from}|${e.to}`;
    if (dirSeen.has(key)) continue;
    dirSeen.add(key);
    unique.push(e);
  }

  const outAdj = new Map<string, string[]>();
  const inAdj = new Map<string, string[]>();
  const neighborsOf = new Map<string, Set<string>>();
  for (const id of ids) {
    outAdj.set(id, []);
    inAdj.set(id, []);
    neighborsOf.set(id, new Set());
  }
  for (const e of unique) {
    outAdj.get(e.from)!.push(e.to);
    inAdj.get(e.to)!.push(e.from);
    neighborsOf.get(e.from)!.add(e.to);
    neighborsOf.get(e.to)!.add(e.from);
  }

  /** Directed closure from `start` following `adj`, never stepping through
   *  `hub`. Includes `start` — a child's own world. */
  const closure = (
    start: string,
    hub: string,
    adj: ReadonlyMap<string, string[]>,
  ): Set<string> => {
    const seen = new Set<string>([start]);
    const queue = [start];
    while (queue.length > 0) {
      const cur = queue.pop()!;
      for (const next of adj.get(cur) ?? []) {
        if (next === hub || seen.has(next)) continue;
        seen.add(next);
        queue.push(next);
      }
    }
    return seen;
  };

  // Candidate wings.
  interface Wing {
    readonly anchorId: string;
    readonly side: "out" | "in";
    readonly candidates: string[];
  }
  const wings: Wing[] = [];
  const inoutAnchors = new Set<string>();
  /** Outside-connection count per accepted (hub, child), keyed `hub|child`. */
  const extOf = new Map<string, number>();

  for (const hub of ids) {
    if (reserved?.has(hub)) continue; // twin-shelf members never anchor
    const neighbors = neighborsOf.get(hub)!;
    if (neighbors.size === 0) continue;

    // Two-way counts as incoming.
    const sideOf = new Map<string, "in" | "out">();
    for (const child of neighbors) {
      sideOf.set(child, dirSeen.has(`${child}|${hub}`) ? "in" : "out");
    }

    // Each child's territory: the directed closure away from the hub.
    const territory = new Map<string, Set<string>>();
    for (const child of neighbors) {
      territory.set(
        child,
        closure(child, hub, sideOf.get(child) === "out" ? outAdj : inAdj),
      );
    }

    // Guilt is ASYMMETRIC: a child is guilty when ITS territory contains a
    // sibling — that child's world builds a path around the hub. The
    // contained sibling shares no blame for being reached, but guilt
    // beyond a tolerated pair POISONS the whole side: the hub is provably
    // not the sole route between that side's children, so no wing may
    // template there — including the innocent members (their real edges
    // to the guilty relay would fight the rigid column). Guilt across
    // sides is a path AROUND the hub and poisons both wings. Deep
    // world-merges outside the family (two subtrees converging on shared
    // infrastructure) don't entangle at all — the outside-connection cap
    // governs those by exclusion, without poisoning.
    const guiltOf = new Map<string, string[]>();
    for (const child of neighbors) {
      const t = territory.get(child)!;
      const guilty: string[] = [];
      for (const other of neighbors) {
        if (other !== child && t.has(other)) guilty.push(other);
      }
      guiltOf.set(child, guilty);
    }
    let outPoisoned = false;
    let inPoisoned = false;
    for (const child of neighbors) {
      const guilt = guiltOf.get(child)!;
      if (guilt.length === 0) continue;
      const side = sideOf.get(child)!;
      if (guilt.some((s) => sideOf.get(s) !== side)) {
        outPoisoned = true;
        inPoisoned = true;
        break;
      }
      // Tolerated entangled PAIR: exactly one same-side sibling in the
      // territory, and that sibling implicates nobody else.
      const other = guilt[0]!;
      const otherGuilt = guiltOf.get(other)!;
      const pairOk =
        guilt.length === 1 &&
        (otherGuilt.length === 0 ||
          (otherGuilt.length === 1 && otherGuilt[0] === child));
      if (!pairOk) {
        if (side === "out") outPoisoned = true;
        else inPoisoned = true;
      }
    }
    // Forward bypass (see the file header): an in-child whose DOWNSTREAM
    // closure reaches an out-sibling routes around the hub in the flow
    // direction — invisible to guilt (which looks upstream for in-children
    // and downstream for out-children) and tolerated by the outside-
    // connection cap (one external edge on each endpoint). Both endpoints
    // are excluded; their bypass edge stays between free nodes instead of
    // freezing across a rigid frame. Relays are caught by their own check
    // (an in-sibling on the bypass path reaches the same out-sibling).
    const bypassExcluded = new Set<string>();
    for (const child of neighbors) {
      if (sideOf.get(child) !== "in") continue;
      const downstream = closure(child, hub, outAdj);
      for (const other of neighbors) {
        if (other === child || sideOf.get(other) !== "out") continue;
        if (downstream.has(other)) {
          bypassExcluded.add(child);
          bypassExcluded.add(other);
        }
      }
    }

    const kept: string[] = [];
    for (const child of neighbors) {
      const poisoned = sideOf.get(child) === "out" ? outPoisoned : inPoisoned;
      if (!poisoned && !bypassExcluded.has(child)) kept.push(child);
    }

    // World-size + outside-connection caps. Each child's outside-connection
    // count is kept (keyed hub|child) — the claim stage needs it: a 1-leaf
    // PAIR demands a fully dedicated leaf (ext 0), while a multi-leaf wing
    // tolerates the cap (column position is dominated by the hub).
    const outChildren: string[] = [];
    const inChildren: string[] = [];
    for (const child of kept) {
      const world = territory.get(child)!;
      // The world cap looks BOTH ways: templating a child under this hub
      // drags the HUB-SIDE assembly, not the child's continuation (the
      // onward edges become external springs on the body). A child with a
      // deep world in only ONE direction — a frontend whose downstream is
      // the whole system, a sink whose upstream is everything — is still
      // a clean wing leaf. Only a CORRIDOR (large worlds on BOTH sides)
      // stays organic: templating it under one hub would misplace it
      // relative to the other heavy side.
      const worldOther = closure(
        child,
        hub,
        sideOf.get(child) === "out" ? inAdj : outAdj,
      );
      if (
        world.size - 1 > CHILD_MAX_WORLD &&
        worldOther.size - 1 > CHILD_MAX_WORLD
      ) {
        continue;
      }
      let ext = 0;
      if (sideOf.get(child) === "out") {
        for (const z of inAdj.get(child)!) {
          if (z !== hub && !world.has(z)) ext++;
        }
        if (ext <= CHILD_MAX_EXTERNAL && !reserved?.has(child)) {
          outChildren.push(child);
          extOf.set(`${hub}|${child}`, ext);
        }
      } else {
        for (const z of outAdj.get(child)!) {
          if (z !== hub && !world.has(z)) ext++;
        }
        if (ext <= CHILD_MAX_EXTERNAL && !reserved?.has(child)) {
          inChildren.push(child);
          extOf.set(`${hub}|${child}`, ext);
        }
      }
    }
    if (outChildren.length > 0) {
      wings.push({ anchorId: hub, side: "out", candidates: outChildren.sort() });
    }
    if (inChildren.length > 0) {
      wings.push({ anchorId: hub, side: "in", candidates: inChildren.sort() });
    }
    if (outChildren.length > 0 && inChildren.length > 0) inoutAnchors.add(hub);
  }

  // SMALLEST wing claims first: a dedicated caller (Payments → its two
  // processors) is the likelier owner of a shared child than a busy
  // orchestrator that happens to also call it. Inout gateways break size
  // ties (a chain interior claims BOTH its neighbors before either
  // neighbor pairs off with the interior). Deterministic remainder.
  wings.sort(
    (a, b) =>
      a.candidates.length - b.candidates.length ||
      Number(inoutAnchors.has(b.anchorId)) - Number(inoutAnchors.has(a.anchorId)) ||
      (a.anchorId < b.anchorId ? -1 : a.anchorId > b.anchorId ? 1 : 0) ||
      (a.side < b.side ? -1 : a.side > b.side ? 1 : 0),
  );

  // A node that anchors a LARGER wing outranks any smaller wing trying to
  // claim it as a leaf — the fan-in hub must not be swallowed by a 1-1
  // pair hanging off its side.
  const anchorPriority = new Map<string, number>();
  for (const wing of wings) {
    const cur = anchorPriority.get(wing.anchorId) ?? 0;
    if (wing.candidates.length > cur) {
      anchorPriority.set(wing.anchorId, wing.candidates.length);
    }
  }

  const satelliteMaxArea = NODE_W * NODE_H * PAIR_LEAF_MAX_AREA_FRAC;
  const isSatellite = (id: string): boolean => {
    if (!sizes) return true;
    const sz = sizes.get(id);
    if (!sz) return true;
    return sz.w * sz.h <= satelliteMaxArea;
  };

  const usedLeaf = new Set<string>();
  const usedAnchor = new Set<string>();
  const motifs: Motif[] = [];
  for (const wing of wings) {
    if (usedLeaf.has(wing.anchorId)) continue;
    let claimed = wing.candidates.filter((id) => {
      if (usedLeaf.has(id)) return false;
      const priority = anchorPriority.get(id) ?? 0;
      // An anchor that already claimed a strictly smaller wing may be
      // swallowed — buildAssemblies flattens its assembly into this
      // wing's slot. Strictness keeps the swallow relation acyclic.
      if (usedAnchor.has(id)) return priority < wing.candidates.length;
      return priority <= wing.candidates.length;
    });
    // Pairs are for satellites, and only DEDICATED ones: a lone full-size
    // leaf stays organic, and so does a satellite with any outside
    // connection — templating it to one caller's side ignores half its
    // edges (the other caller's edge would stab the rigid pair from
    // across the board). Multi-leaf wings keep the ext cap instead.
    if (
      claimed.length === 1 &&
      (!isSatellite(claimed[0]!) ||
        (extOf.get(`${wing.anchorId}|${claimed[0]!}`) ?? 0) > 0)
    ) {
      claimed = [];
    }
    if (claimed.length === 0) continue;
    usedAnchor.add(wing.anchorId);
    for (const id of claimed) usedLeaf.add(id);
    motifs.push({ anchorId: wing.anchorId, leafIds: claimed, side: wing.side });
  }
  // Stable output order: by anchor, out before in.
  motifs.sort(
    (a, b) =>
      (a.anchorId < b.anchorId ? -1 : a.anchorId > b.anchorId ? 1 : 0) ||
      (a.side === b.side ? 0 : a.side === "in" ? -1 : 1),
  );
  return motifs;
}

// ---- Rigid assemblies -------------------------------------------------------

/** Leaves per column before the template switches to a balanced grid. */
const GRID_COLUMN_THRESHOLD = 6;
/** Shelves switch to a grid earlier — a LONG sink wall on a mesh flank
 *  blocks more than it organizes. 3 (not 2): a 1×3 column is part of the
 *  era-R3 reference render's look; the 2-threshold trial gridded every
 *  shelf and contributed to the carpeted core it was reverted with. */
const SHELF_GRID_COLUMN_THRESHOLD = 3;
/** Vertical gap between stacked leaves; matches the organize pass. */
const ASSEMBLY_GAP_Y = 40;
/** Horizontal gap between grid columns of one wing. */
const ASSEMBLY_GAP_X = 60;
/** Clearance between a ported hub's wall-port band and the nearest wing leaf.
 *  A ported hub's out-port pills stack at the hub's cy and run to the wall; the
 *  out-wing sits in that lane. We open a band-sized gap in the wing's vertical
 *  centre so the hub→pill connectors run through empty space (and mirror for the
 *  in-wing / in-port band). This is the clearance on each side of that gap. */
const WING_BAND_CLEARANCE = 32;

interface AssemblyBase {
  /** Super-node id in the coarse graph. */
  readonly superId: string;
  /** All member ids (swallowed sub-assemblies flattened). */
  readonly memberIds: readonly string[];
  /** Member top-left offsets inside the assembly frame. */
  readonly offsets: ReadonlyMap<string, { dx: number; dy: number }>;
  /** Assembly frame size (flow space). */
  readonly w: number;
  readonly h: number;
}

/** A gateway formation: hub + wings. The hub is a member and the body's
 *  pin/warm-start/team anchor. */
export interface GatewayAssembly extends AssemblyBase {
  readonly kind: "gateway";
  readonly hubId: string;
}

/** A hubless sink stack. No member owns it; warm-start anchors on the
 *  first member (by id) that has prior geometry, and it can never be
 *  pinned as a body (a pinned member leaves the shelf instead). */
export interface ShelfAssembly extends AssemblyBase {
  readonly kind: "shelf";
  readonly patronId: string;
}

/** Discriminated union — call sites must handle both kinds explicitly
 *  (an optional hubId would silently no-op through Map.get(undefined)). */
export type MotifAssembly = GatewayAssembly | ShelfAssembly;

/**
 * Pre-solve assemblies for the given motifs (grouped by anchor — an
 * anchor's in/out wings share one assembly). `size` must be the FLOW-space
 * size map; `idealGapOf(hubId, leafId)` returns the per-edge ideal
 * silhouette gap (same source the springs use).
 *
 * A leaf that itself anchors a motif in the set is a SWALLOWED
 * sub-assembly: its wing slot takes the child assembly's bounding box and
 * the child's members are splatted into the parent at composed offsets
 * (fan-of-fans, flat output). Parents build children-first by recursion —
 * wing sizes are NOT a reliable order (claiming and the call sites'
 * pinned/port filters both shrink wings after the swallow was granted).
 * The swallow relation is a forest: an anchor can be claimed as a leaf
 * only after it finished anchoring, and a leaf is claimed at most once.
 *
 * `shelves` (already disjoint from motif members — detection runs after
 * gateway claiming) each become one hubless assembly: a centered column
 * grid of the members, nothing else. Shelves neither swallow nor get
 * swallowed (no anchor to claim).
 */
export function buildAssemblies(
  motifs: readonly Motif[],
  size: ReadonlyMap<string, { w: number; h: number }>,
  idealGapOf: (a: string, b: string) => number,
  shelves: readonly Shelf[] = [],
  // Per-hub wall-port band heights (0 = no ports on that side). A ported hub's
  // wings are split around its band so no spoke sits in the hub→pill lane. The
  // default (no ports) makes buildAssemblies byte-identical to the port-blind
  // template — non-ported assemblies are untouched.
  portBandOf: (hubId: string) => { inBand: number; outBand: number } = () => ({
    inBand: 0,
    outBand: 0,
  }),
): MotifAssembly[] {
  const byAnchor = new Map<string, Motif[]>();
  for (const m of motifs) {
    const arr = byAnchor.get(m.anchorId) ?? [];
    arr.push(m);
    byAnchor.set(m.anchorId, arr);
  }

  const anchors = [...byAnchor.keys()].sort();

  /** Finished assemblies by hub id; swallowed ones are consumed. */
  const built = new Map<string, MotifAssembly>();
  /** Anchors already constructed — consumed ones must not rebuild. */
  const done = new Set<string>();
  const buildFor = (anchorId: string): void => {
    if (done.has(anchorId)) return;
    done.add(anchorId);
    const wings = byAnchor.get(anchorId)!;
    // Children first: any leaf that itself anchors motifs must exist in
    // `built` before its slot is laid out. Terminates — the swallow
    // relation is a forest (see the doc comment above).
    for (const wing of wings) {
      for (const id of wing.leafIds) {
        if (byAnchor.has(id)) buildFor(id);
      }
    }
    const hub = size.get(anchorId)!;

    /** Slot dims: a swallowed sub-assembly occupies its whole bbox. */
    const dimOf = (id: string): { w: number; h: number } =>
      built.get(id) ?? size.get(id)!;

    /** Lay one wing's leaves into balanced columns; returns the wing's
     *  bbox size plus per-member offsets relative to the wing's top-left
     *  (sub-assembly members splatted at composed offsets). Slots align
     *  to the column's hub-facing side — "start" (left) for out-wings,
     *  "end" (right) for in-wings — so mixed-width slots keep their
     *  edge-attach sides flush. */
    const layoutWing = (
      leafIds: readonly string[],
      align: "start" | "end",
      band: number,
    ) => {
      const cols = Math.ceil(leafIds.length / GRID_COLUMN_THRESHOLD);
      const perCol = Math.ceil(leafIds.length / cols);
      const colWidths: number[] = [];
      const colHeights: number[] = [];
      const slots = new Map<string, { col: number; row: number }>();
      leafIds.forEach((id, i) => {
        const col = Math.floor(i / perCol);
        const row = i % perCol;
        slots.set(id, { col, row });
        const s = dimOf(id);
        colWidths[col] = Math.max(colWidths[col] ?? 0, s.w);
        colHeights[col] =
          (colHeights[col] ?? 0) + s.h + (row > 0 ? ASSEMBLY_GAP_Y : 0);
      });
      const width =
        colWidths.reduce((a, b) => a + b, 0) + ASSEMBLY_GAP_X * (cols - 1);
      const height0 = Math.max(...colHeights);
      const colX: number[] = [];
      let x = 0;
      for (let c = 0; c < cols; c++) {
        colX[c] = x;
        x += colWidths[c]! + ASSEMBLY_GAP_X;
      }
      // Pass 1: place each TOP-LEVEL leaf (columns vertically centred). Kept as
      // whole leaves (not yet splatted) so a swallowed sub-assembly moves as a
      // unit when the band gap opens — never torn across the hub's port lane.
      const rowY: number[] = colWidths.map(() => 0);
      const placed: { id: string; dx: number; dy: number; h: number }[] = [];
      for (const id of leafIds) {
        const { col } = slots.get(id)!;
        const s = dimOf(id);
        const yBase = (height0 - colHeights[col]!) / 2;
        const dx = colX[col]! + (align === "end" ? colWidths[col]! - s.w : 0);
        placed.push({ id, dx, dy: yBase + rowY[col]!, h: s.h });
        rowY[col] = rowY[col]! + s.h + ASSEMBLY_GAP_Y;
      }
      // `anchorY` is the wing-local y that maps to the hub's centre. Without a
      // band that is the bbox centre (current behaviour, byte-identical). With a
      // band the wing splits around its centre: leaves whose own centre is at/
      // above the wing centre rigidly shift UP until they clear the band's top,
      // the rest shift DOWN past its bottom — opening an empty hub→pill lane.
      let height = height0;
      let anchorY = height0 / 2;
      if (band > 0 && placed.length > 0) {
        const half = band / 2 + WING_BAND_CLEARANCE;
        const center = height0 / 2;
        let maxBottomUp = -Infinity;
        let minTopDown = Infinity;
        const upSide = new Map<string, boolean>();
        for (const p of placed) {
          const up = p.dy + p.h / 2 <= center;
          upSide.set(p.id, up);
          if (up) maxBottomUp = Math.max(maxBottomUp, p.dy + p.h);
          else minTopDown = Math.min(minTopDown, p.dy);
        }
        const shiftUp =
          maxBottomUp > -Infinity ? Math.max(0, maxBottomUp - (center - half)) : 0;
        const shiftDown =
          minTopDown < Infinity ? Math.max(0, center + half - minTopDown) : 0;
        for (const p of placed) p.dy += upSide.get(p.id) ? -shiftUp : shiftDown;
        // Re-origin the wing to top-left; the hub-centre anchor rides with it.
        let minY = Infinity;
        let maxY = -Infinity;
        for (const p of placed) {
          minY = Math.min(minY, p.dy);
          maxY = Math.max(maxY, p.dy + p.h);
        }
        for (const p of placed) p.dy -= minY;
        height = maxY - minY;
        anchorY = center - minY;
      }
      // Pass 2: splat swallowed sub-assemblies at the (possibly shifted) leaf dy.
      const offsets = new Map<string, { dx: number; dy: number }>();
      for (const p of placed) {
        const child = built.get(p.id);
        if (child) {
          for (const [mid, o] of child.offsets) {
            offsets.set(mid, { dx: p.dx + o.dx, dy: p.dy + o.dy });
          }
        } else {
          offsets.set(p.id, { dx: p.dx, dy: p.dy });
        }
      }
      return { width, height, offsets, anchorY };
    };

    /** Ideal hub-to-wing EDGE gap: max per-leaf spring gap. Placing wings
     *  by edge gap (not center distance) keeps the internal clearance
     *  exact for any node aspect ratio — never below the separation
     *  guarantee. */
    const wingGap = (leafIds: readonly string[]): number => {
      let g = 0;
      for (const id of leafIds) g = Math.max(g, idealGapOf(anchorId, id));
      return g;
    };

    const outWing = wings.find((w) => w.side === "out");
    const inWing = wings.find((w) => w.side === "in");
    // The in-wing straddles the hub's IN-port band (left wall lane); the
    // out-wing straddles the OUT-port band (right wall lane). Each wing splits
    // around its own side's band only.
    const { inBand, outBand } = portBandOf(anchorId);
    // The lane a wing must clear is at least the hub's OWN height: the hub→pill
    // connectors emanate across the hub face near the hub, then fan to the pill
    // stack at the wall. Mirror the P0 metric's max(o.h, band) so the split
    // provably zeroes it.
    const outEff = outBand > 0 ? Math.max(hub.h, outBand) : 0;
    const inEff = inBand > 0 ? Math.max(hub.h, inBand) : 0;
    const outL = outWing ? layoutWing(outWing.leafIds, "start", outEff) : undefined;
    const inL = inWing ? layoutWing(inWing.leafIds, "end", inEff) : undefined;

    // Frame: [in-wing] ←gap→ [hub] ←gap→ [out-wing]. Everything is aligned so
    // each wing's `anchorY` (its bbox centre, or the band-gap centre when
    // ported) lands on the hub's centre. Frame height = the hub's reach above/
    // below its centre unioned with each wing's. With no ports every anchorY is
    // a bbox centre and this reduces to plain vertical centring (byte-identical).
    const offsets = new Map<string, { dx: number; dy: number }>();
    const aboveOf = (w?: { height: number; anchorY: number }) => (w ? w.anchorY : 0);
    const belowOf = (w?: { height: number; anchorY: number }) =>
      w ? w.height - w.anchorY : 0;
    const frameTop = Math.max(hub.h / 2, aboveOf(inL), aboveOf(outL));
    const frameBottom = Math.max(hub.h / 2, belowOf(inL), belowOf(outL));
    const frameH = frameTop + frameBottom;

    const placeWing = (
      wing: { width: number; offsets: Map<string, { dx: number; dy: number }>; anchorY: number },
      atX: number,
    ) => {
      const yBase = frameTop - wing.anchorY;
      for (const [id, o] of wing.offsets) {
        offsets.set(id, { dx: atX + o.dx, dy: yBase + o.dy });
      }
    };

    let hubX = 0;
    if (inL) {
      placeWing(inL, 0);
      hubX = inL.width + wingGap(inWing!.leafIds);
    }
    offsets.set(anchorId, { dx: hubX, dy: frameTop - hub.h / 2 });
    if (outL) {
      placeWing(outL, hubX + hub.w + wingGap(outWing!.leafIds));
    }

    let maxX = -Infinity;
    for (const [id, o] of offsets) {
      const s = size.get(id)!;
      maxX = Math.max(maxX, o.dx + s.w);
    }

    // Consume swallowed sub-assemblies — their members now live here.
    for (const wing of wings) {
      for (const id of wing.leafIds) built.delete(id);
    }

    const memberIds = [...offsets.keys()].sort();
    built.set(anchorId, {
      kind: "gateway",
      superId: `~motif~${anchorId}`,
      hubId: anchorId,
      memberIds,
      offsets,
      w: maxX,
      h: frameH,
    });
  };
  for (const anchorId of anchors) buildFor(anchorId);
  const out: MotifAssembly[] = [...built.values()];

  // ---- Shelves: hubless centered column grids -------------------------
  for (const shelf of shelves) {
    const members = shelf.memberIds;
    const cols = Math.ceil(members.length / SHELF_GRID_COLUMN_THRESHOLD);
    const perCol = Math.ceil(members.length / cols);
    const colWidths: number[] = [];
    const colHeights: number[] = [];
    const slots = new Map<string, { col: number; row: number }>();
    members.forEach((id, i) => {
      const col = Math.floor(i / perCol);
      const row = i % perCol;
      slots.set(id, { col, row });
      const s = size.get(id)!;
      colWidths[col] = Math.max(colWidths[col] ?? 0, s.w);
      colHeights[col] =
        (colHeights[col] ?? 0) + s.h + (row > 0 ? ASSEMBLY_GAP_Y : 0);
    });
    const w =
      colWidths.reduce((a, b) => a + b, 0) + ASSEMBLY_GAP_X * (cols - 1);
    const h = Math.max(...colHeights);
    const offsets = new Map<string, { dx: number; dy: number }>();
    const colX: number[] = [];
    let x = 0;
    for (let c = 0; c < cols; c++) {
      colX[c] = x;
      x += colWidths[c]! + ASSEMBLY_GAP_X;
    }
    const rowY: number[] = colWidths.map(() => 0);
    for (const id of members) {
      const { col } = slots.get(id)!;
      const s = size.get(id)!;
      const yBase = (h - colHeights[col]!) / 2;
      // Centered in the column — a shelf has no hub-facing side.
      offsets.set(id, {
        dx: colX[col]! + (colWidths[col]! - s.w) / 2,
        dy: yBase + rowY[col]!,
      });
      rowY[col] = rowY[col]! + s.h + ASSEMBLY_GAP_Y;
    }
    out.push({
      kind: "shelf",
      // First member disambiguates a patron carrying both a twin shelf
      // and an overlap shelf (member sets are disjoint, so this is
      // unique and stable while membership holds).
      superId: `~shelf~${shelf.patronId}~${shelf.memberIds[0]!}`,
      patronId: shelf.patronId,
      memberIds: [...members].sort(),
      offsets,
      w,
      h,
    });
  }

  out.sort((a, b) => (a.superId < b.superId ? -1 : a.superId > b.superId ? 1 : 0));
  return out;
}
